import { type ChangeEvent } from "react";
import type { FieldPath, FieldPathValue, FieldValues, RegisterOptions } from "react-hook-form";
import { useController } from "react-hook-form";

import { FieldCharacterCount } from "@/design-system/FieldFeedback";
import { Input, type InputProps } from "@/design-system/Input";
import { mergeRefs } from "@/helpers/mergeRefs";

import { FormErrorWrapper } from "./FormErrorWrapper";

export interface FormInputProps<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
> extends Omit<InputProps, "name" | "ref"> {
  name: TName;
  rules?: RegisterOptions<TFormValues, TName>;
  onChange?: (value: ChangeEvent<HTMLInputElement>) => void;
  "data-testid"?: string;
  defaultValue?: FieldPathValue<TFormValues, TName>;
  inputRef?: React.Ref<HTMLInputElement>;
  hideErrorText?: boolean;
}

/**
 * When using input type="number", the value will always be a string. If you need to use a different type, you have to convert it to a string yourself (during submit).
 */
export function FormInput<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
>({
  name,
  rules,
  defaultValue,
  inputRef,
  hideErrorText,
  ...props
}: FormInputProps<TFormValues, TName>): React.ReactNode {
  const {
    field: { ref, ...field },
    fieldState: { error },
  } = useController<TFormValues, TName>({
    name,
    rules,
    defaultValue,
  });
  const maxLengthValue = typeof rules?.maxLength === "object" ? rules.maxLength.value : undefined;
  const isError = error != null;
  const currentValueLength = `${field.value || ""}`.length || 0;

  return (
    <FormErrorWrapper
      subtext={maxLengthValue != null && <FieldCharacterCount current={currentValueLength} maximum={maxLengthValue} />}
      {...{
        hideErrorText,
        name,
      }}
    >
      <Input
        ref={mergeRefs(ref, inputRef)}
        {...props}
        {...field}
        value={(field.value as any) || ""}
        onChange={(e) => {
          field.onChange(e);
          props.onChange?.(e);
        }}
        onFocus={(e) => {
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          field.onBlur();
          props.onBlur?.(e);
        }}
        {...{
          isError,
        }}
      />
    </FormErrorWrapper>
  );
}
